/* React and Libraries */
import { TableColumn } from "react-data-table-component";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "react-feather";
import Swal from "sweetalert2";
import { Badges, Btn } from "../../AbstractElements";

/* Types */
import { ViewEmployeeDeployments } from "../../Types/Employee/EmployeeDeployments";
import { CustomCellInterFace } from "../../Types/TablesType";
import { CRUDPermissions } from "../../Types/Common/Access";

/* Services */
import { employeeDeploymentService } from "../../Services/employeeDeployments";

const ActionCell = ({
  row,
  totalRecords,
  access,
}: {
  row: ViewEmployeeDeployments;
  totalRecords: number;
  access: CRUDPermissions;
}) => {
  const queryClient = useQueryClient();

  const isDeleteDisabled = totalRecords <= 1;

  const handleStatusToggle = () => {
    if (isDeleteDisabled) return;

    Swal.fire({
      icon: "question",
      title: "Delete Record?",
      html: "Are you sure you want to <b>delete</b> this record?",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-check"></i> Delete',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        toggleMutation.mutate(row.id);
      }
    });
  };

  const toggleMutation = useMutation({
    mutationFn: employeeDeploymentService.toggleStatus,

    onMutate: () => {
      Swal.fire({
        title: "Processing Request",
        text: "The system is processing the transaction. Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries();

      Swal.close();

      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "Employee record has been updated successfully.",
        confirmButtonColor: "#4e73df",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Refreshing Employee Information",
            text: "The system is refreshing the updated employee information. Please wait...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();

              setTimeout(() => {
                Swal.close();
              }, 2000);
            },
          });
        }
      });
    },

    onError: (error: any) => {
      Swal.close();

      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text:
          error.response?.data?.message ||
          "The system was unable to complete the transaction. Please try again.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  return (
    <div className="d-flex align-items-center gap-2" hidden={!access.update}>
      <Btn
        size="sm"
        color="danger"
        className="px-2 d-inline-flex align-items-center justify-content-center"
        onClick={handleStatusToggle}
        disabled={isDeleteDisabled || toggleMutation.isPending}
        title={
          isDeleteDisabled
            ? "At least one employee deployment must remain."
            : "Delete Employee Deployment"
        }
      >
        <Trash2 size={12} className="me-1" />
        Delete
      </Btn>
    </div>
  );
};

const CustomBadge: React.FC<CustomCellInterFace> = ({ label, color }) => (
  <Badges style={{ width: "105px" }} pill color={color}>
    {label}
  </Badges>
);

export const EmpDeploymentsTableColumn = (
  totalRecords: number,
  activeDeploymentId: number | undefined,
  access: CRUDPermissions,
): TableColumn<ViewEmployeeDeployments>[] => [
  {
    name: "COMPANY & ROLE",
    selector: (row) => row.company_name,
    sortable: true,
    center: false,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        <b>{row.company_name}</b>
        {"\n"}
        {row.role_name}
      </div>
    ),
  },
  {
    name: "DIVISION & DEPARTMENT",
    selector: (row) => row.division_name,
    sortable: true,
    center: false,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        <b>{row.division_name}</b>
        {"\n"}
        {row.department_name}
      </div>
    ),
  },
  {
    name: "REGION & CLUSTER - DEPLOYMENT SITE",
    selector: (row) => row.region_name,
    sortable: true,
    center: false,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        <b>{row.region_name}</b>
        {"\n"}
        {row.cluster_name} - {row.deployment_site_name}
      </div>
    ),
  },
  {
    name: "EFFECTIVITY DATE",
    selector: (row) => row.effectivity_date ?? "",
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.effectivity_date
          ? new Date(row.effectivity_date).toLocaleDateString("en-US", {
              month: "long",
              day: "2-digit",
              year: "numeric",
            })
          : "-"}
      </div>
    ),
    sortable: true,
    center: false,
    width: "200px",
  },
  {
    name: "STATUS",
    cell: (row: ViewEmployeeDeployments) => {
      const now = new Date();
      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");

      const isUpcoming = row.effectivity_date > today;
      const isActive = row.id === activeDeploymentId;

      const label = isUpcoming ? "UPCOMING" : isActive ? "ACTIVE" : "INACTIVE";

      const color = isUpcoming ? "info" : isActive ? "success" : "danger";

      return <CustomBadge color={color} label={label} />;
    },
    width: "120px",
    sortable: true,
    center: true,
  },
  {
    name: "DATE MODIFIED",
    selector: (row) => row.updated_date ?? "",
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.updated_by_user}
        {"\n"}
        {row.updated_date}
      </div>
    ),
    sortable: true,
    width: "200px",
    center: false,
  },
  {
    name: "ACTION",
    sortable: false,
    center: false,
    width: "100px",
    cell: (row) => (
      <ActionCell row={row} totalRecords={totalRecords} access={access} />
    ),
  },
];
